import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { StatusPills } from '@/shared/components/StatusCounts';
import type { StatusCounts } from '@/shared/types/domain';

interface MinistryRowProps {
  id: string;
  name: string;
  /** US-09: cada ministério exibe seu líder responsável atual. */
  leaderName: string | null;
  description?: string | null;
  /**
   * Contagem por situação, **opcional de propósito**: quem passa é o Início,
   * onde a linha resume o que a pessoa tem em mãos. Na aba Coordenações ela
   * não é passada — lá a linha responde estrutura (quem lidera o quê), e a
   * contagem chegava recortada pela `projects_select`, zerada nos ministérios
   * irmãos. Ver o docblock do `CoordinationCard` e a ação 6 da ADR-012.
   */
  counts?: StatusCounts;
  /**
   * A faixa azul rente à borda esquerda, **opcional pela mesma razão que a
   * contagem é**: ela é o eco em miniatura da faixa do `CoordinationCard`, e
   * serve para dizer que esta lista é o *dentro* daquele card — o que só faz
   * sentido na tela de detalhe da coordenação, onde o usuário acabou de vir
   * de lá.
   *
   * No Início ela fica de fora de propósito: lá a linha carrega as
   * `StatusPills`, e o painel gasta matiz para dizer SITUAÇÃO de projeto
   * (spec-calendar-ux). Uma tarja colorida encostada em pills coloridas vira
   * uma segunda cor competindo pela mesma leitura.
   */
  accent?: boolean;
}

export function MinistryRow({
  id,
  name,
  leaderName,
  description,
  counts,
  accent = false,
}: MinistryRowProps) {
  return (
    <li>
      <Link
        to={`/ministerios/${id}`}
        className="group flex items-stretch transition-colors duration-fast ease-out hover:bg-accent/60 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
      >
        {/* Decorativa: nome e destino já estão no texto ao lado, então a faixa
            não acrescenta nada a quem usa leitor de tela. Ela vai colada na
            borda (fora do `p-4`) e de altura cheia — recuada ou centrada
            viraria um marcador de item, e o que se quer é a aresta do card. */}
        {accent ? (
          <div
            className="w-1 shrink-0 self-stretch bg-gradient-to-b from-primary to-primary-hover"
            aria-hidden="true"
          />
        ) : null}

        <div className="flex min-w-0 flex-1 items-center gap-3 p-4">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-medium">{name}</p>
            <p className="text-sm text-muted-foreground">
              {/* Ministério sem líder vigente é dado real, não erro de carga: a
                  troca de liderança abre uma janela em que ninguém está no posto,
                  e esconder isso atrapalha justamente quem precisa resolver. */}
              {leaderName ? `Liderança: ${leaderName}` : 'Sem liderança vigente cadastrada'}
            </p>
            {/* Uma linha só, truncada: aqui a descrição é pista de reconhecimento
                numa lista, não leitura. Quem quer o texto inteiro abre o
                ministério. Sem descrição, a linha não existe. */}
            {description ? (
              <p className="line-clamp-1 text-sm text-muted-foreground">{description}</p>
            ) : null}
            {counts ? <StatusPills counts={counts} /> : null}
          </div>
          {/* A seta anda 2px para a direita no hover. É o menor movimento do
              painel e o que mais diz: "esta linha LEVA a algum lugar". */}
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </div>
      </Link>
    </li>
  );
}
